import {isEqual} from 'lodash';
import {batch} from 'react-redux';

import {setRunningMethod} from '@/actions/autoProfileStateAction';
import {setDevicePropertyName} from '@/actions/deviceInfoActions';
import {showLoading} from '@/actions/loadingActions';
import {
  IsShowManualControlAvailablePopUp,
  IsShowManualControlPopUp,
  setStatusManualControl,
} from '@/actions/manualControlActions';
import {hidePopUp} from '@/actions/modalActions';
import {
  setDeltaTime,
  setScheduleId,
  setScheduleRun,
  setScheduleType,
  setStartTime,
} from '@/actions/schedulerStateAction';
import {
  initValue,
  isCompleteFill,
  saveDeviceVersion,
  setAdvSetupList,
  setAdvSetupSelected,
  SetCycleFinishStatus,
  SetErrorParams,
  setLastAdvSetupIdCreated,
  updateAutoProfileSetupMode,
  updateCurrentAdvSetupParam,
  updateCurrentProfileParam,
  updateDefaultConfigAdvSetupParams,
  updateDefaultHighPressureParam,
  updateDefaultHighSequenceParam,
  updateDefaultLowPressureParam,
  updateDefaultLowSequenceParam,
  updateDefaultPressureTemplate,
  updateDefaultReturnValvePressureParams,
  updateDefaultSequenceTemplate,
  updateEstimateTime,
  updateFontSize,
  updateLockAccess,
  updatePercentageProgressBar,
  updateShowThresholdError,
  updateStatus,
} from '@/actions/setupActions';
import {getAdvSetupsApi, getDefaultAdvSetupApi, getSelectedAdvSetupsApi} from '@/api/advSetupApi';
import {getDelayProfile} from '@/api/delayProfileApi';
import {getConfiguration, getStatus} from '@/api/deviceApi';
import {getDefaultReturnValvePressure} from '@/api/returnValvePressureApi';
import {getDisplaySettingSystem, getInfoSystem} from '@/api/systemApi';
import {MembraneCutOff, RecoveryOfLiquidAction} from '@/constants/autoProfileEnum';
import {ResponseData} from '@/constants/dataEnum';
import {CURRENT_RUNNING_MODE} from '@/constants/enums';
import HTTPStatusCode from '@/constants/httpStatusCode';
import * as APPDATA from '@/constants/pcConstAppData';
import {LockStatusPageEvent, RecoveryOfLiquidEvent} from '@/constants/pubSubEnum';
import {RUN_TIME_MODE} from '@/contents/manualProfile/constant/sequenceContentConstants';
import {GetPathOfPage, isAllAutoProfileParamFilled} from '@/libs/appHelper';
import {getAutoProfileModeOnRunningProfile, getAutoProfileUpdateStatusModel} from '@/libs/autoProfileHelper';
import {convertTimeHHMMSS} from '@/libs/timeHelper';
import apiObserver from '@/observers/apiObserver';
import {eventBusObject} from '@/observers/eventBus';

import {refillBufferVolumeOnInit} from './refillBufferVolBusiness';
import {getSystemStatusUpdated} from './systemUpdateBusiness';

// eslint-disable-next-line import/prefer-default-export
export const initApp = () => async (dispatch, getState) => {
  try {
    const bus = eventBusObject();
    const {setupStore} = {...getState()};
    const {lockAccess} = setupStore;

    const [
      resInfo,
      responseConfig,
      responseStatus,
      responseDisplaySettings,
      resDefaultReturnPressure,
      resDefaultAdvSetupApi,
      resDelayProfile,
    ] = await Promise.all([
      getInfoSystem(),
      getConfiguration(),
      getStatus(),
      getDisplaySettingSystem(),
      getDefaultReturnValvePressure(),
      getDefaultAdvSetupApi(),
      getDelayProfile(),
    ]);

    // getAutoTimeZoneSystem already implement when open the setting page

    dispatch(getSystemStatusUpdated());
    dispatch(initUpdateStatus());
    dispatch(fetchDelayProfile());

    if (resInfo.status === HTTPStatusCode.OK) {
      const {HardwareVersion, HostName} = resInfo.data;

      batch(() => {
        dispatch(setDevicePropertyName(HostName, true));
        dispatch(saveDeviceVersion(HardwareVersion));
      });
    } else {
      apiObserver.notify.errorResponseHandler(resInfo);
    }

    let profileData = null;
    if (resDelayProfile.data !== ResponseData.NOT_FOUND) {
      profileData = resDelayProfile.data.ProfileConfig;
    } else if (responseConfig.status === HTTPStatusCode.OK) {
      profileData = responseConfig.data;
    }

    if (profileData) {
      const autoProfileMode = getAutoProfileModeOnRunningProfile(
        profileData[APPDATA.KEY.DiafiltrationMode],
        profileData[APPDATA.KEY.MoreThan50ml],
      );
      dispatch(updateAutoProfileSetupMode(autoProfileMode));
      dispatch(setRunningMethod(autoProfileMode));
    }

    if (responseConfig.status === HTTPStatusCode.OK) {
      batch(() => {
        dispatch(
          initValue({
            bufferVolumeToUse: responseConfig.data[APPDATA.KEY.BufferVolumeToUse],
            diafiltrationMode: responseConfig.data[APPDATA.KEY.DiafiltrationMode],
            diaphragmPressure: responseConfig.data[APPDATA.KEY.DiaphragmPressure],
            diaphragmTimingOpen: responseConfig.data[APPDATA.KEY.DiaphragmTimingOpen],
            diaphragmTimingClose: responseConfig.data[APPDATA.KEY.DiaphragmTimingClose],
            diaphragmVacuumPressure: responseConfig.data[APPDATA.KEY.DiaphragmVacuumPressure],
            estimatedFinalConcentration: responseConfig.data[APPDATA.KEY.EstimatedFinalConcentration],
            finalVolume: responseConfig.data[APPDATA.KEY.FinalVolume],
            flowRate: responseConfig.data[APPDATA.KEY.FlowRate],
            operatingPressure: responseConfig.data[APPDATA.KEY.OperatingPressure],
            primeTiming: responseConfig.data[APPDATA.KEY.PrimeTiming],
            recoveryOfLiquid: responseConfig.data[APPDATA.KEY.RecoveryOfLiquid],
            startingVolume: responseConfig.data[APPDATA.KEY.StartingVolume],
            startingConcentration: responseConfig.data[APPDATA.KEY.StartingConcentration],
            valveTiming: responseConfig.data[APPDATA.KEY.ValveTiming],
            volumeToStartAt: responseConfig.data[APPDATA.KEY.VolumeToStartAt],
            // dummyBuffer : response.data[APPDATA.KEY.DUMMY_BUFFER],
            dummyVolume: responseConfig.data[APPDATA.KEY.DUMMY_VOLUME],
            currentBuffer: '0',
            currentVolume: '0',
            currentPCState:
              responseStatus.data.currentRunningMode === APPDATA.ENUM.CURRENT_RUNNING_MODE.AUTO_PROFILE
                ? responseStatus.data[APPDATA.KEY.DeviceStatus]
                : APPDATA.DEVICE_STATE.IDLE,
            prevPCState:
              responseStatus.data.currentRunningMode === APPDATA.ENUM.CURRENT_RUNNING_MODE.AUTO_PROFILE
                ? responseStatus.data[APPDATA.KEY.DeviceStatus]
                : APPDATA.DEVICE_STATE.IDLE,
            isPCRunning: !!(
              responseStatus.data[APPDATA.KEY.DeviceStatus] !== APPDATA.DEVICE_STATE.IDLE &&
              responseStatus.data.currentRunningMode === APPDATA.ENUM.CURRENT_RUNNING_MODE.AUTO_PROFILE
            ),
            isPCPaused: !!(
              responseStatus.data[APPDATA.KEY.DeviceStatus] === APPDATA.DEVICE_STATE.PAUSED &&
              responseStatus.data.currentRunningMode === APPDATA.ENUM.CURRENT_RUNNING_MODE.AUTO_PROFILE
            ),
          }),
        );

        dispatch(getPCLocked(responseStatus.data[APPDATA.KEY.Locked]));
      });
      bus.publish(LockStatusPageEvent.ON_CLICK_LOCK_CONCENTRATOR, {
        isLockAccess: lockAccess,
        isDisabled: responseStatus.data[APPDATA.KEY.DeviceStatus] === APPDATA.PC_IDLE_STATE,
      });
      if (responseStatus.data[APPDATA.KEY.DeviceStatus] === APPDATA.PC_RECOVERING_STATE)
        bus.publish(RecoveryOfLiquidEvent.SHOW_POP_UP, {
          recoveryAction: RecoveryOfLiquidAction.START_RECOVERY_OF_LIQUID,
        });
    } else {
      apiObserver.notify.errorResponseHandler(responseConfig);
    }

    if (responseDisplaySettings.status === HTTPStatusCode.OK) {
      dispatch(updateFontSize(responseDisplaySettings.data[APPDATA.KEY.FontSize]));
    } else {
      apiObserver.notify.errorResponseHandler(responseDisplaySettings);
    }

    if (resDefaultReturnPressure.status === HTTPStatusCode.OK) {
      dispatch(updateDefaultReturnValvePressureParams(resDefaultReturnPressure.data));
    } else {
      apiObserver.notify.errorResponseHandler(resDefaultReturnPressure);
    }

    if (resDefaultAdvSetupApi.status === HTTPStatusCode.OK) {
      dispatch(generateTemplateNewAdvSetup(resDefaultAdvSetupApi.data));
    } else {
      apiObserver.notify.errorResponseHandler(resDefaultAdvSetupApi);
    }
  } catch (error) {
    console.log('init app error', error);
    console.error(error);
    apiObserver.notify.errorHandler(error);
  }

  return true;
};

const generateTemplateNewAdvSetup = (defaultConfigParams) => async (dispatch) => {
  const templateNewAdvSetup = {
    [APPDATA.KEY.Id]: -1,
    [APPDATA.KEY.Name]: 'No Advanced Setup',
    [APPDATA.KEY.CreatedAt]: '00/00/2018 00:00:00',
    [APPDATA.KEY.ModifiedAt]: '00/00/2018 00:00:00',
    [APPDATA.KEY.ConfigAdvSetup]: {
      [APPDATA.KEY.GeneralAdvSetup]: {
        [APPDATA.KEY.MembraneFillingCycle]: -1,
        [APPDATA.KEY.SkipPriming]: -1,
        [APPDATA.KEY.ManualControlThreshold]: -1,
      },
      [APPDATA.KEY.MainAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: -1,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
      },
      [APPDATA.KEY.DiafiltrationAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: -1,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
        [APPDATA.KEY.Duration]: -1,
      },
      [APPDATA.KEY.PrimeAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: -1,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
        [APPDATA.KEY.Duration]: -1,
      },
      [APPDATA.KEY.RecoveryAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: -1,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
        [APPDATA.KEY.Duration]: -1,
      },
      [APPDATA.KEY.TubeMixingAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: 0,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
        [APPDATA.KEY.Duration]: -1,
      },
      [APPDATA.KEY.RecirculateAdvSetup]: {
        [APPDATA.KEY.PressureAdvSetup]: {},
        [APPDATA.KEY.DefaultReturnValvePressure]: null,
        [APPDATA.KEY.DiaphragmTimingOpen]: -1,
        [APPDATA.KEY.DiaphragmTimingClose]: -1,
        [APPDATA.KEY.ValveTiming]: -1,
        [APPDATA.KEY.Duration]: -1,
      },
    },
  };
  if (defaultConfigParams !== undefined) {
    templateNewAdvSetup[APPDATA.KEY.Id] = defaultConfigParams[APPDATA.KEY.Id];
    templateNewAdvSetup[APPDATA.KEY.Name] = defaultConfigParams[APPDATA.KEY.Name];
    templateNewAdvSetup[APPDATA.KEY.CreatedAt] = defaultConfigParams[APPDATA.KEY.CreatedAt];
    templateNewAdvSetup[APPDATA.KEY.ModifiedAt] = defaultConfigParams[APPDATA.KEY.ModifiedAt];
    if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup] !== undefined) {
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.GeneralAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.GeneralAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.GeneralAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.DiafiltrationAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.DiafiltrationAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.DiafiltrationAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecoveryAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecoveryAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecoveryAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.TubeMixingAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.TubeMixingAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.TubeMixingAdvSetup];
      if (defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecirculateAdvSetup])
        templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecirculateAdvSetup] =
          defaultConfigParams[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.RecirculateAdvSetup];
    }
  }

  const templateLowPressure =
    templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup][APPDATA.KEY.PressureAdvSetup];
  const templateHighPressure =
    templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup][APPDATA.KEY.PressureAdvSetup];
  const templatePressure = {
    Id: '-1',
    Sequence: 'pressure_changes',
    Repeat: '1',
    Active: true,
    Running: false,
    SequenceParams: {
      PressureControllerParam: templateLowPressure,
    },
  };
  const templateLowSeq = {
    [APPDATA.KEY.DiaphragmTimingOpen]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup][APPDATA.KEY.DiaphragmTimingOpen],
    [APPDATA.KEY.DiaphragmTimingClose]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup][APPDATA.KEY.DiaphragmTimingClose],
    [APPDATA.KEY.ValveTiming]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.PrimeAdvSetup][APPDATA.KEY.ValveTiming],
  };
  const templateHighSeq = {
    [APPDATA.KEY.DiaphragmTimingOpen]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup][APPDATA.KEY.DiaphragmTimingOpen],
    [APPDATA.KEY.DiaphragmTimingClose]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup][APPDATA.KEY.DiaphragmTimingClose],
    [APPDATA.KEY.ValveTiming]:
      templateNewAdvSetup[APPDATA.KEY.ConfigAdvSetup][APPDATA.KEY.MainAdvSetup][APPDATA.KEY.ValveTiming],
  };
  const templateSequence = {
    Id: '-1', // ignore compare
    Sequence: 'prime', // ignore to compare
    Repeat: 7,
    Active: true,
    Running: false,
    SequenceParams: {
      Hi: templateHighSeq,
      Lo: templateLowSeq,
    },
    // not used in Backend
    PressureState: {
      PressureType: 'Lo', // ignore to compare
      PressureConfig: {
        Hi: templateHighPressure,
        Lo: templateLowPressure,
      },
    },
    RepeatType: RUN_TIME_MODE.TIMES.VALUE,
    TempRepeat: 1,
  };

  batch(() => {
    dispatch(updateCurrentAdvSetupParam(templateNewAdvSetup));
    dispatch(updateDefaultConfigAdvSetupParams(templateNewAdvSetup));
    dispatch(setAdvSetupSelected(templateNewAdvSetup));
    dispatch(updateDefaultHighPressureParam(templateHighPressure));
    dispatch(updateDefaultLowPressureParam(templateLowPressure));
    dispatch(updateDefaultHighSequenceParam(templateHighSeq));
    dispatch(updateDefaultLowSequenceParam(templateLowSeq));
    dispatch(updateDefaultPressureTemplate(templatePressure));
    dispatch(updateDefaultSequenceTemplate(templateSequence));
  });

  // NOTE: this is unnecessary implementation
  await dispatch(initAdvSetup());
};

const getPCLocked = (isPCLock) => (dispatch, getState) => {
  const {setupStore} = {...getState()};
  const {lockAccess, isPCRunning} = setupStore;
  const bus = eventBusObject();

  if (isPCLock !== lockAccess) {
    bus.publish(LockStatusPageEvent.ON_CLICK_LOCK_CONCENTRATOR, {
      isLockAccess: isPCLock,
      isDisabled: !isPCRunning,
    });
    dispatch(updateLockAccess(isPCLock));
  }
};

// #region UPDATE STATUS

const checkIsManualControlEnable = (previouseStatusManualControl, newStatusOfManualControl) => (dispatch, getState) => {
  const {setupStore, manualControlStore} = {...getState()};
  const {profileRunning, isPCRunning} = setupStore;
  const {IsShowManualControlAvailablePopUp: _IsShowManualControlAvailablePopUp} = manualControlStore;

  const FinalVolume = Number(profileRunning.Config.FinalVolume);
  const bus = eventBusObject();

  if (!isPCRunning) return;

  if (previouseStatusManualControl.IsManualControlEnable === APPDATA.ENUM.MANUAL_CONTROL_STATE.DISABLE) {
    if (newStatusOfManualControl.IsManualControlEnable === APPDATA.ENUM.MANUAL_CONTROL_STATE.ENABLE) {
      dispatch(showLoading(false));
      if (_IsShowManualControlAvailablePopUp) {
        if (FinalVolume < 3) dispatch(IsShowManualControlPopUp(true));
      } else {
        // eslint-disable-next-line no-lonely-if
        if (FinalVolume < 3) {
          const activePageAddress = GetPathOfPage(window.location.href);
          if (activePageAddress !== APPDATA.AUTO_PROFILE_STATUS) window.location.href = APPDATA.AUTO_PROFILE_STATUS;
          dispatch(IsShowManualControlAvailablePopUp(true));
          dispatch(IsShowManualControlPopUp(true));
        } else {
          bus.publish(RecoveryOfLiquidEvent.SHOW_POP_UP, {
            recoveryAction: RecoveryOfLiquidAction.STOP_MANUAL_CONTROL,
          });
        }
      }
    }
  }
};

const handleUpdateStatus = (response) => async (dispatch, getState) => {
  const {setupStore, manualControlStore, loadingStore, modalStore} = {...getState()};
  const {
    profileRunning,
    isPCRunning,
    currentPressure,
    pumpFlowRate,
    currentVolume,
    estimateConcentration,
    estSecondsRemaining,
    secondsElapsed,
    warningMessage,
    currentBuffer,
    failureMessage,
    concentrationFlowRate,
    currentPressureSensors,
    currentVolumeChangesThreshold,
    ErrorVolumeChanges,
    currentTiming,
    activeMenu,
    percentageProgressBar,
    dialfiltrationStatus,
    errorParams,
    estimateTime,
  } = setupStore;
  const {showLoading: isShowLoading} = loadingStore;
  const {popUpVisible, popUpTitle} = modalStore;
  const {StatusManualControl: statusManualControl} = manualControlStore;

  const percentage = isPCRunning ? response.data[APPDATA.KEY.PercentageComplate] : 0;
  const estimateTimeInSeconds = response.data[APPDATA.KEY.EstSecondRemaining];
  const newStatus = getAutoProfileUpdateStatusModel(response.data);
  const oldStatus = {
    currentPressure,
    pumpFlowRate,
    currentVolume,
    estimateConcentration,
    estSecondsRemaining,
    secondsElapsed,
    warningMessage,
    currentBuffer,
    failureMessage,
    profileRunning,
    concentrationFlowRate,
    currentPressureSensors,
    currentVolumeChangesThreshold,
    ErrorVolumeChanges,
    currentTiming,
  };

  if (response.data[APPDATA.KEY.DeviceStatus] !== APPDATA.DEVICE_STATE.IDLE) {
    const {currentRunningMode} = response.data;

    switch (currentRunningMode) {
      case APPDATA.ENUM.CURRENT_RUNNING_MODE.AUTO_PROFILE:
        if (
          !(
            activeMenu === APPDATA.SETUP ||
            activeMenu === APPDATA.STATUS ||
            activeMenu === APPDATA.AUTO_PROFILE_SETUP_TAB ||
            activeMenu === APPDATA.AUTO_PROFILE_STATUS_TAB ||
            activeMenu === APPDATA.VOLUME_SENSOR_CHART ||
            activeMenu === APPDATA.AUTO_PROFILE_PARAMETERS_TAB
          )
        ) {
          window.location.href = APPDATA.AUTO_PROFILE_STATUS;

          if (response.data?.profileRunning?.Config) {
            const autoProfileMode = getAutoProfileModeOnRunningProfile(
              response.data.profileRunning.Config.DiafiltrationMode,
              response.data.profileRunning.Config.MoreThan50ml,
            );
            batch(() => {
              dispatch(updateAutoProfileSetupMode(autoProfileMode));
              dispatch(setRunningMethod(autoProfileMode));
            });
          }
        }
        break;

      case CURRENT_RUNNING_MODE.MANUAL_PROFILE:
        if (activeMenu !== APPDATA.MANUAL_PROFILE) {
          window.location.href = APPDATA.MANUAL_PROFILE_ADDRESS;
        }
        break;

      case CURRENT_RUNNING_MODE.CLEANING_WIZARD:
        if (activeMenu !== APPDATA.CLEANING_WIZARD) {
          window.location.href = APPDATA.CLEANING_WIZARD_ADDRESS;
        }
        break;
      default:
        break;
    }
  }

  // TODO: this comparison is useless, it will changes the setup store status all the time (it causing the page will always re-render)
  if (isEqual(newStatus, oldStatus) !== true) dispatch(updateStatus(newStatus));

  if (percentageProgressBar !== percentage) dispatch(updatePercentageProgressBar(percentage));

  if (
    isShowLoading &&
    popUpVisible &&
    (popUpTitle === APPDATA.MESSAGES.MANUAL_CONTROL.TITLE ||
      popUpTitle === APPDATA.MESSAGES.MANUAL_CONTROL_RECOVERY_OF_LIQUID.TITLE)
  ) {
    dispatch(hidePopUp());
  }

  // #region estimate time
  const timeHHMMSS = Number.isNaN(parseInt(estimateTimeInSeconds, 10))
    ? estimateTimeInSeconds
    : convertTimeHHMMSS(estimateTimeInSeconds);

  if (timeHHMMSS !== estimateTime) dispatch(updateEstimateTime(timeHHMMSS));
  // #endregion

  const newStatusManualControl = response.data[APPDATA.KEY.ManualControlStatus];
  const previouseStatusManualControl = statusManualControl;

  if (isEqual(newStatusManualControl, previouseStatusManualControl) !== true) {
    dispatch(setStatusManualControl(newStatusManualControl));
    dispatch(checkIsManualControlEnable(previouseStatusManualControl, newStatusManualControl));
  }

  // cek need refill
  const newDialfiltrationStatus = response.data.dialfiltrationStatus;
  const prevDialfiltrationStatus = dialfiltrationStatus;

  if (isEqual(newDialfiltrationStatus, prevDialfiltrationStatus) !== true) {
    dispatch(refillBufferVolumeOnInit(newDialfiltrationStatus, newStatus.profileRunning, newStatus.currentBuffer));
  }

  //   if (this._uiPerformance !== undefined && !isRemoteView) this._uiPerformance.checkRefreshPage();
  const newErrorParams = response.data[APPDATA.KEY.ErrorParams];
  if (!isEqual(newErrorParams, errorParams)) {
    dispatch(SetErrorParams(newErrorParams));
    dispatch(updateShowThresholdError(true));
  }

  if (newErrorParams !== null) {
    switch (newErrorParams.Code) {
      case APPDATA.ERROR_DICTIONARY.VolumeThreshold:
        dispatch(SetErrorParams(newErrorParams));
        dispatch(updateShowThresholdError(true));
        break;
      default:
        console.warn('Error code is undefined. Your code is : ', newErrorParams.Code);
        break;
    }
  }

  dispatch(SetCycleFinishStatus(response.data.isCycleFinish));
  // Check Lock Access
  dispatch(getPCLocked(response.data[APPDATA.KEY.Locked]));
};

const fetchDelayProfile = () => async (dispatch) => {
  const response = await getDelayProfile();
  if (response?.data !== 'NotFound') dispatch(setInitDelayProfile(response));
};

const setInitDelayProfile = (resDelayProfile) => async (dispatch) => {
  const responseConfig = resDelayProfile?.data?.ProfileConfig;
  const membraneCutOffValue = MembraneCutOff[responseConfig?.MembraneCutOffSize] || MembraneCutOff.CHIP_5_KDA;
  const currentProfileParameters = {
    StartingVolume: responseConfig?.StartingVolume,
    StartingConcentration: responseConfig?.StartingConcentration,
    EstimatedFinalConcentration: responseConfig?.EstimatedFinalConcentration,
    FinalVolume: responseConfig?.FinalVolume,
    BufferVolumeToUse: responseConfig?.BufferVolumeToUse,
    VolumeToStartAt: responseConfig?.VolumeToStartAt,
    DiafiltrationMode: responseConfig?.DiafiltrationMode,
    [APPDATA.KEY.ExchangeStepSize]: responseConfig?.ExchangeStepSize,
    [APPDATA.KEY.StepSizeVolume]: responseConfig?.StepSizeVolume,
    [APPDATA.KEY.MembraneCutOffSize]: membraneCutOffValue,
    [APPDATA.KEY.MembraneType]: String(responseConfig?.MembraneType),
    [APPDATA.KEY.ConicalVolume]: responseConfig?.ConicalVolume,
    [APPDATA.KEY.TotalBufferVolumeToUse]: responseConfig?.TotalBufferVolumeToUse,
    [APPDATA.KEY.ChipValve]: responseConfig?.ChipValve,
    [APPDATA.KEY.MoreThan50ml]: String(responseConfig?.MoreThan50ml),
    [APPDATA.KEY.StartingVolumeFor2ndTube]: responseConfig?.StartingVolumeFor2ndTube,
    [APPDATA.KEY.IdAdvSetup]: responseConfig?.IdAdvancedSetup,
    [APPDATA.KEY.AdvancedSetupName]: responseConfig?.AdvancedSetupName,
  };
  const selectedAdvSetup = await getSelectedAdvSetupsApi(resDelayProfile?.data?.ProfileConfig?.IdAdvancedSetup);
  batch(() => {
    dispatch(setScheduleRun(true));
    dispatch(setScheduleId(resDelayProfile?.data?.Id));
    dispatch(setScheduleType(resDelayProfile?.data?.ScheduleType));
    dispatch(setStartTime(resDelayProfile?.data?.StartTime));
    dispatch(setDeltaTime(resDelayProfile?.data?.DeltaTime));
    dispatch(updateCurrentProfileParam(currentProfileParameters));
    dispatch(isCompleteFill(isAllAutoProfileParamFilled(currentProfileParameters) === true));
    dispatch(updateCurrentAdvSetupParam(selectedAdvSetup?.data));

    window.location.href = APPDATA.AUTO_PROFILE_SETUP;
  });
};

const initUpdateStatus = () => async (dispatch, getState) => {
  const {setupStore, deviceStore} = {...getState()};
  const {cleaningProperties, isManualProfileRunning} = setupStore;
  const {Power: DevicePower} = deviceStore;

  if (!isManualProfileRunning && !cleaningProperties.isCleaningWizardRunning) {
    if (DevicePower === APPDATA.POWER.OFF) return;

    const response = await getStatus();
    dispatch(handleUpdateStatus(response));
  }
};

// #endregion

const initAdvSetup = () => async (dispatch) => {
  const response = await getAdvSetupsApi();

  if (response.status === HTTPStatusCode.OK) {
    const advSetupList = response.data?.data;

    if (advSetupList === undefined) return;

    batch(() => {
      dispatch(setAdvSetupList(advSetupList));
      dispatch(setLastAdvSetupIdCreated(advSetupList.length));
    });
  } else {
    apiObserver.notify.errorResponseHandler(response);
  }
};
